import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import {
  getMcOpsState,
  saveMcOpsState,
  flushMcOpsState,
  placeCall,
  postEscalation,
  sendClientSms,
  generateConfirmationPdf,
  syncJobEvent,
  syncNotesHub,
  postTeamNote,
  postDailySummary,
  sendClientEmail,
  sendDocument,
  mcLogout,
  fetchTemplates,
  fetchCoordinators,
  fetchTimeOff,
  applyViewAs,
} from "../../business/redux/reducers/mcOpsDashboard/actions";
import "./mcOps.css";
import { Icon } from "./icons";
import { LOCALE, todayStr, uid } from "./lib/format";
import { computeDailySummary, needsAttentionCount } from "./lib/selectors";
import { useNotesHubSync } from "./lib/useNotesHubSync";
import { McOpsContext, clone } from "./lib/McOpsContext";
import { useMcOpsActions } from "./lib/useMcOpsActions";
import { LandingPage } from "./pages/LandingPage";
import { AssignmentLogPage } from "./pages/AssignmentLogPage";
import { CoordinatorsPage } from "./pages/CoordinatorsPage";
import { JobPage } from "./pages/JobPage";
import { LocalMovesPage } from "./pages/LocalMovesPage";
import { LdJobsPage } from "./pages/LdJobsPage";
import { MovesProgressPage } from "./pages/MovesProgressPage";
import { PostMovePage } from "./pages/PostMovePage";
import { NeedsAttentionPage } from "./pages/NeedsAttentionPage";
import { RemindersPage } from "./pages/RemindersPage";
import { KnowledgeHubPage } from "./pages/KnowledgeHubPage";
import { RoadwayBuddyPage } from "./pages/RoadwayBuddyPage";
import { MgmtSummaryPage } from "./pages/MgmtSummaryPage";
import { ProgressScreen } from "./components/ProgressScreen";
import { DailySummaryModal } from "./components/DailySummaryModal";
import { ReminderHubModal } from "./components/ReminderHubModal";
import { JobModal } from "./modal/JobModal";
import { ComposeMessageModal } from "./modal/ComposeMessageModal";
import { TemplatesModal } from "./components/TemplatesModal";
import { DriverAssignHost } from "./components/DriverAssignModal";
import { LoginGate } from "./components/LoginGate";
import { LoadErrorScreen } from "./components/LoadErrorScreen";
import { getMcToken } from "../../business/redux/reducers/mcOpsDashboard/apiBase";

const SIDEBAR_KEY = "mc_sidebar_collapsed";

/** Where the "Back to KPI" button sends the coordinator. */
const BACK_PATH = "/kpi";

const NAV = [
  { group: "Overview" },
  { page: "landing", icon: "home", label: "Landing" },
  { group: "Jobs to be Confirmed" },
  { page: "jobs-confirm", icon: "clipboard", label: "Local Moves" },
  { page: "ld-jobs", icon: "truck", label: "Long Distance Jobs" },
  { group: "Active Moves" },
  { page: "moves-progress", icon: "inprogress", label: "Moves in Progress" },
  { page: "post-move", icon: "check", label: "Post Move" },
  { group: "Notifications" },
  { page: "needs-attention", icon: "alert", label: "Needs Attention" },
  { page: "reminders", icon: "bell", label: "Reminders" },
  { page: "coverage", icon: "clock", label: "Roadway Buddy" },
  { group: "Resources" },
  { page: "knowledge-hub", icon: "book", label: "Knowledge Hub" },
  { group: "Insights" },
  { page: "mgmt", icon: "chart", label: "Mgmt Summary" },
  { group: "Admin", adminOnly: true },
  { page: "coordinators", icon: "inprogress", label: "Coordinators", adminOnly: true },
  { page: "assignment-log", icon: "clipboard", label: "Assignment log", adminOnly: true },
];

const PAGES = {
  landing: LandingPage,
  "jobs-confirm": LocalMovesPage,
  "ld-jobs": LdJobsPage,
  "moves-progress": MovesProgressPage,
  "post-move": PostMovePage,
  "needs-attention": NeedsAttentionPage,
  reminders: RemindersPage,
  coverage: RoadwayBuddyPage,
  "knowledge-hub": KnowledgeHubPage,
  mgmt: MgmtSummaryPage,
  coordinators: CoordinatorsPage,
  "assignment-log": AssignmentLogPage,
};

const greeting = () => {
  const h = new Date().getHours();
  return h < 12 ? "Good morning" : h < 18 ? "Good afternoon" : "Good evening";
};

export const McOpsDashboardPage = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { jobId: routeJobId } = useParams();
  const location = useLocation();
  const store = useSelector((s) => s.mcOpsDashboard);
  const state = store?.data;
  const mcName = (store?.meta?.mc?.name || "").trim() || "Miles";

  const [currentPage, setCurrentPage] = useState("landing");
  const [collapsed, setCollapsed] = useState(
    () => localStorage.getItem(SIDEBAR_KEY) === "1",
  );
  const [activeJobId, setActiveJobId] = useState(null);
  const [activeTab, setActiveTab] = useState("s1");
  const [naFilter, setNaFilter] = useState("all");
  const [calPop, setCalPop] = useState(null);
  const [progressOpen, setProgressOpen] = useState(false);
  const [summaryOpen, setSummaryOpen] = useState(false);
  const [summarySent, setSummarySent] = useState(false);
  const [reminderHubOpen, setReminderHubOpen] = useState(false);
  const [hasToken, setHasToken] = useState(() => !!getMcToken());
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const [toast, setToast] = useState(null);
  const [compose, setCompose] = useState(null); // {channel, template, jobId} for the send popup
  const [templatesOpen, setTemplatesOpen] = useState(false);
  const [driverAssignJobId, setDriverAssignJobId] = useState(null); // job being assigned to a driver
  const authRequired = store?.meta?.authRequired;
  const saveFailedAt = store?.meta?.saveFailedAt;
  const mcInitial = (mcName || "M").trim().charAt(0).toUpperCase() || "M";
  const canSwitch = store?.meta?.mc?.canSwitch === true;
  const attentionCount = needsAttentionCount(state);
  // Notes Hub → Zoho: push the open job's notes summary when it changes.
  const activeJobForNotes = (state?.jobs || []).find((j) => j.id === activeJobId) || null;
  const pushNotesHub = useCallback((payload) => dispatch(syncNotesHub(payload)), [dispatch]);
  useNotesHubSync(activeJobForNotes, pushNotesHub);
  const coordinators = store?.meta?.coordinators || [];
  const viewingAs = store?.meta?.mc?.viewingAs || "";

  useEffect(() => {
    if (!hasToken) return;
    dispatch(getMcOpsState({ showLoading: true }));
    dispatch(fetchTemplates());
    dispatch(fetchCoordinators());
    dispatch(fetchTimeOff());
  }, [dispatch, hasToken]);

  // Near-real-time board sync: quietly re-pull the shared state every 45s so two
  // coordinators on the same board see each other's changes without a manual
  // refresh. Paused while the tab is hidden or a job modal is open, so in-flight
  // edits are never clobbered.
  useEffect(() => {
    const REFRESH_MS = 45000;
    const id = setInterval(() => {
      if (typeof document !== "undefined" && document.hidden) return;
      if (activeJobId || routeJobId || !hasToken) return;
      dispatch(getMcOpsState({ showLoading: false }));
    }, REFRESH_MS);
    return () => clearInterval(id);
  }, [dispatch, activeJobId, routeJobId, hasToken]);

  const persist = useCallback(
    (next) => {
      dispatch(saveMcOpsState(next));
    },
    [dispatch],
  );

  // Per-field "Saved" feedback lives on the note fields themselves; the only
  // global toast is a failure alert so a lost save is never silent.
  useEffect(() => {
    if (!saveFailedAt) return;
    setToast({ kind: "error", text: "Couldn’t save — check your connection" });
    const t = setTimeout(() => setToast(null), 3200);
    return () => clearTimeout(t);
  }, [saveFailedAt]);

  // Real outcome of a "Send text": the send is otherwise optimistic, so an
  // actual Talkdesk failure would look like a success. Toast the truth (and the
  // error) so a text that never reached the client is never silent.
  const smsResult = store?.meta?.smsResult;
  useEffect(() => {
    if (!smsResult || !smsResult.at) return;
    setToast(
      smsResult.ok
        ? { kind: "success", text: "Text sent" }
        : { kind: "error", text: "Text failed — " + (smsResult.error || "unknown error") },
    );
    const t = setTimeout(() => setToast(null), smsResult.ok ? 2600 : 6000);
    return () => clearTimeout(t);
  }, [smsResult?.at]);

  // Same truthful feedback for "Send email".
  const emailResult = store?.meta?.emailResult;
  useEffect(() => {
    if (!emailResult || !emailResult.at) return;
    setToast(
      emailResult.ok
        ? { kind: "success", text: "Email sent" }
        : { kind: "error", text: "Email failed — " + (emailResult.error || "unknown error") },
    );
    const t = setTimeout(() => setToast(null), emailResult.ok ? 2600 : 6000);
    return () => clearTimeout(t);
  }, [emailResult?.at]);

  // Feedback for notes / actions that sync to Zoho: confirm the write, flag a
  // failure with its reason, or warn that a job isn't linked to Zoho at all.
  const noteResult = store?.meta?.noteResult;
  useEffect(() => {
    if (!noteResult || !noteResult.at) return;
    const isNote = noteResult.event === "note";
    setToast(
      noteResult.ok
        ? { kind: "success", text: isNote ? "Note synced to Zoho" : "Synced to Zoho" }
        : { kind: "error", text: (isNote ? "Note not synced — " : "Zoho sync failed — ") + (noteResult.error || "unknown error") },
    );
    const t = setTimeout(() => setToast(null), noteResult.ok ? 2600 : 6500);
    return () => clearTimeout(t);
  }, [noteResult?.at]);

  // Saves are debounced/coalesced, so a pending edit can still be in the queue
  // when the tab is closed or refreshed. Flush it with a keepalive request on
  // unload so the last change (e.g. a note just typed) is never lost.
  useEffect(() => {
    const flush = () => dispatch(flushMcOpsState());
    window.addEventListener("pagehide", flush);
    window.addEventListener("beforeunload", flush);
    return () => {
      window.removeEventListener("pagehide", flush);
      window.removeEventListener("beforeunload", flush);
    };
  }, [dispatch]);

  // Live outbound side-effects (Talkdesk call/SMS, Cliq escalation) fired
  // alongside the local state mutations inside useMcOpsActions.
  const effects = useMemo(
    () => ({
      placeCall: (phone) => dispatch(placeCall(phone)),
      postEscalation: (payload) => dispatch(postEscalation(payload)),
      sendClientSms: (payload) => dispatch(sendClientSms(payload)),
      generateConfirmationPdf: (job) => dispatch(generateConfirmationPdf(job)),
      syncJobEvent: (payload) => dispatch(syncJobEvent(payload)),
      postTeamNote: (payload) => dispatch(postTeamNote(payload)),
      sendClientEmail: (payload) => dispatch(sendClientEmail(payload)),
      sendDocument: (payload) => dispatch(sendDocument(payload)),
    }),
    [dispatch],
  );

  const actions = useMcOpsActions(state, persist, effects);

  const toggleSidebar = () => {
    setCollapsed((prev) => {
      localStorage.setItem(SIDEBAR_KEY, prev ? "0" : "1");
      return !prev;
    });
  };

  const openJob = useCallback((id, tab) => {
    setActiveJobId(id);
    setActiveTab(tab || "s1");
  }, []);

  const ctx = useMemo(() => {
    const mutate = (fn) => {
      const next = clone(state);
      fn(next);
      persist(next);
    };

    return {
      state,
      actions,
      // Roadway Buddy: time off requests, for the cover tag on the job cards.
      timeOff: store?.meta?.timeOff || null,
      naFilter,
      setNaFilter,
      calPop,
      toggleCalPop: (slug) => setCalPop((p) => (p === slug ? null : slug)),
      progressOpen,
      openProgressScreen: () => setProgressOpen(true),
      closeProgressScreen: () => setProgressOpen(false),
      summaryOpen,
      closeSummary: () => setSummaryOpen(false),
      openSummary: () => setSummaryOpen(true),
      summarySent,
      reminderHubOpen,
      openReminderHub: () => setReminderHubOpen(true),
      closeReminderHub: () => setReminderHubOpen(false),

      activeJob: state.jobs.find((j) => j.id === activeJobId) || null,
      activeTab,
      setActiveTab,
      openJob,
      closeModal: () => setActiveJobId(null),

      // Compose/send popup lives at the page root (not inside the job modal,
      // whose backdrop-filter would trap a position:fixed child off-screen).
      openCompose: (payload) => setCompose(payload),
      closeCompose: () => setCompose(null),

      // "Assign to Driver Delivery" popup, also at the page root.
      driverAssignJobId,
      openDriverAssign: (jobId) => setDriverAssignJobId(jobId),
      closeDriverAssign: () => setDriverAssignJobId(null),

      recordSearch: (jobId) =>
        mutate((d) => {
          d.searchHistory = (d.searchHistory || []).filter((id) => id !== jobId);
          d.searchHistory.unshift(jobId);
          d.searchHistory = d.searchHistory.slice(0, 10);
        }),

      addReminder: actions.addReminder,

      completeReminder: (id) =>
        mutate((d) => {
          const r = d.reminders.find((x) => x.id === id);
          if (r) r.completed = true;
        }),

      toggleEscalation: (id) =>
        mutate((d) => {
          const esc = d.escalations.find((e) => e.id === id);
          if (!esc) return;
          esc.status = esc.status === "open" ? "resolved" : "open";
          if (esc.status === "resolved") esc.resolvedAt = new Date().toISOString();
        }),

      attendItem: (itemId, itemType, jobId) => {
        mutate((d) => {
          if (itemType === "external") {
            const n = d.notifExternal.find((x) => x.id === itemId);
            if (n) n.unattended = false;
          } else if (itemType === "escalation") {
            const esc = d.escalations.find((x) => x.id === itemId);
            if (esc) {
              esc.status = "resolved";
              esc.resolvedAt = new Date().toISOString();
            }
          }
        });
        if (jobId) {
          setActiveJobId(jobId);
          setActiveTab("s1");
        }
      },

      sendSummary: () => {
        const s = computeDailySummary(state);
        setSummarySent(true);
        dispatch(postDailySummary({ text: s.paragraph }));
        mutate((d) => {
          d.notifInternal.unshift({
            id: uid(),
            type: "response",
            text: "Daily activity summary sent to manager: " + s.paragraph,
            jobId: null,
            date: new Date().toISOString(),
          });
        });
      },
    };
  }, [
    state,
    actions,
    store?.meta?.timeOff,
    persist,
    naFilter,
    calPop,
    progressOpen,
    summaryOpen,
    summarySent,
    reminderHubOpen,
    driverAssignJobId,
    activeJobId,
    activeTab,
    openJob,
  ]);

  if (!hasToken || authRequired) {
    return <LoginGate onLoggedIn={() => setHasToken(true)} />;
  }

  // Nothing loaded yet and the backend failed: show the error, never sample data.
  // (A failed refresh after a good load keeps the last board; see the footer note.)
  if (store?.meta?.error && store?.meta?.source === null && !state?.jobs?.length) {
    return (
      <LoadErrorScreen
        error={store.meta.error}
        onRetry={() => dispatch(getMcOpsState({ showLoading: true }))}
      />
    );
  }

  if (!state) return null;

  // Shared popups and notices, rendered on the boards and on a job page alike.
  const overlays = (
    <>
      {toast ? (
        <div className={`mc-toast mc-toast-${toast.kind}`} role="status">
          <span className="mc-toast-ic">
            <Icon name={toast.kind === "success" ? "check" : "alert"} />
          </span>
          <span>{toast.text}</span>
        </div>
      ) : null}

      {compose && state.jobs.find((j) => j.id === compose.jobId) ? (
        <ComposeMessageModal
          job={state.jobs.find((j) => j.id === compose.jobId)}
          channel={compose.channel}
          template={compose.template}
          coordinator={mcName}
          onSend={(p) =>
            actions.sendComposedMessage(compose.jobId, { ...p, sentBy: mcName })
          }
          onClose={() => setCompose(null)}
        />
      ) : null}

      {templatesOpen ? <TemplatesModal onClose={() => setTemplatesOpen(false)} /> : null}

      <DriverAssignHost />
    </>
  );

  // /mc-ops-dashboard/job/<id>?tab=<section>: the job's own page.
  if (routeJobId) {
    return (
      <McOpsContext.Provider value={ctx}>
        <div className="mc-ops-root" lang="en">
          <JobPage
            jobId={routeJobId}
            initialTab={new URLSearchParams(location.search).get("tab") || "s1"}
            loading={!store?.meta?.loaded}
          />
          {overlays}
        </div>
      </McOpsContext.Provider>
    );
  }

  const overdueCount = state.reminders.filter(
    (r) => !r.completed && r.date < todayStr(),
  ).length;
  const PageComponent = PAGES[currentPage] || LandingPage;

  return (
    <McOpsContext.Provider value={ctx}>
      <div
        className="mc-ops-root"
        lang="en"
        onClick={(e) => {
          if (!e.target.closest || !e.target.closest(".cal-pop")) setCalPop(null);
        }}
      >
        <div className="app-shell">
          <div className={`sidebar${collapsed ? " collapsed" : ""}`}>
            <div className="brand">
              <div className="brand-mark">MC</div>
              <div className="brand-text">
                <div className="t1">
                  Move Coordinator Operations{" "}
                  <span
                    style={{
                      background: "rgba(255,255,255,0.14)",
                      border: "1px solid rgba(255,255,255,0.3)",
                      borderRadius: 999,
                      padding: "1px 8px",
                      fontSize: "9.5px",
                      fontWeight: 800,
                      marginLeft: 4,
                      verticalAlign: "middle",
                    }}
                  >
                    v1.1
                  </span>
                </div>
                <div className="t2">Roadway Moving</div>
              </div>
            </div>

            <div className="sidebar-toolbar">
              <button
                type="button"
                className="sidebar-back"
                title="Back to KPI"
                onClick={() => navigate(BACK_PATH)}
              >
                <Icon name="reply" />
                <span>Back to KPI</span>
              </button>
              <button
                type="button"
                className="sidebar-toggle"
                title="Collapse sidebar"
                onClick={toggleSidebar}
              >
                <svg
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M15 6l-6 6 6 6" />
                </svg>
              </button>
            </div>

            <div>
              {NAV.filter((item) => !item.adminOnly || canSwitch).map((item, i) =>
                item.group ? (
                  <div className="nav-group-label" key={`g-${i}`}>
                    {item.group}
                  </div>
                ) : (
                  <button
                    className={`tab-btn${currentPage === item.page ? " active" : ""}`}
                    key={item.page}
                    onClick={() => setCurrentPage(item.page)}
                  >
                    <Icon name={item.icon} />
                    <span>{item.label}</span>
                    {item.page === "needs-attention" && attentionCount ? (
                      <span className="nav-count" title="Missed calls, unanswered texts and overdue reminders">
                        {attentionCount}
                      </span>
                    ) : null}
                  </button>
                ),
              )}
            </div>

            <div className="sidebar-footer">
              <div className="note">
                Move Coordinator Operations — production build.
                <br />
                {store?.meta?.error
                  ? "Last refresh failed — showing the last data received."
                  : "Connected to roadway-backend."}
              </div>
            </div>
          </div>

          <div className="main-wrap">
            <div className="topbar">
              <div className="topbar-greet">
                <div className="g1">{greeting()}, {mcName}</div>
                <div className="g2">
                  {new Date().toLocaleDateString(LOCALE, {
                    weekday: "long",
                    month: "long",
                    day: "numeric",
                  })}
                </div>
              </div>
              <div className="topbar-right">
                <button
                  className="btn-set-reminder"
                  type="button"
                  onClick={() => setReminderHubOpen(true)}
                >
                  <span className="btn-set-reminder-icon">
                    <Icon name="bell" />
                  </span>
                  Set Reminder
                  {overdueCount ? (
                    <span className="reminder-badge-count">{overdueCount}</span>
                  ) : null}
                </button>

                {canSwitch ? (
                  <div className={`mc-switch${viewingAs ? " mc-switch-on" : ""}`} title="View as coordinator">
                    <Icon name="inprogress" />
                    <select
                      className="mc-switch-sel"
                      value={viewingAs || ""}
                      onChange={(e) => dispatch(applyViewAs(e.target.value))}
                    >
                      <option value="">All jobs · Admin</option>
                      {coordinators.map((c) => (
                        <option value={c.id} key={c.id}>
                          {c.name} ({c.jobs})
                        </option>
                      ))}
                    </select>
                  </div>
                ) : null}

                <div style={{ position: "relative" }}>
                  <button
                    className="avatar-round"
                    type="button"
                    title={mcName}
                    onClick={() => setUserMenuOpen((v) => !v)}
                    style={{ border: "none", cursor: "pointer", padding: 0 }}
                  >
                    {mcInitial}
                  </button>
                  {userMenuOpen ? (
                    <div
                      style={{
                        position: "absolute",
                        right: 0,
                        top: "115%",
                        background: "#fff",
                        borderRadius: 10,
                        boxShadow: "0 12px 34px rgba(0,0,0,0.2)",
                        padding: 8,
                        minWidth: 180,
                        zIndex: 60,
                      }}
                    >
                      <div
                        style={{
                          padding: "6px 10px",
                          fontSize: 12.5,
                          fontWeight: 700,
                          color: "#1e2a3a",
                          borderBottom: "1px solid #eef1f6",
                          marginBottom: 4,
                        }}
                      >
                        {mcName}
                      </div>
                      <button
                        type="button"
                        onClick={() => {
                          setUserMenuOpen(false);
                          setTemplatesOpen(true);
                        }}
                        style={{
                          width: "100%",
                          textAlign: "left",
                          padding: "8px 10px",
                          border: "none",
                          background: "transparent",
                          color: "#1e2a3a",
                          fontWeight: 600,
                          fontSize: 13,
                          cursor: "pointer",
                          borderRadius: 8,
                          display: "flex",
                          alignItems: "center",
                          gap: 8,
                        }}
                      >
                        <Icon name="note" /> Message Templates
                      </button>
                      <button
                        type="button"
                        onClick={() => {
                          setUserMenuOpen(false);
                          dispatch(mcLogout());
                          setHasToken(false);
                        }}
                        style={{
                          width: "100%",
                          textAlign: "left",
                          padding: "8px 10px",
                          border: "none",
                          background: "transparent",
                          color: "#c0392b",
                          fontWeight: 600,
                          fontSize: 13,
                          cursor: "pointer",
                          borderRadius: 8,
                        }}
                      >
                        Log out
                      </button>
                    </div>
                  ) : null}
                </div>
              </div>
            </div>

            <PageComponent />
          </div>
        </div>

        {currentPage === "landing" ? (
          <button
            className="btn primary fab-summary"
            type="button"
            onClick={() => setSummaryOpen(true)}
          >
            <Icon name="mail" />
            Send Daily Summary to Manager
          </button>
        ) : null}

        <ProgressScreen />
        <JobModal />
        <DailySummaryModal />
        <ReminderHubModal />

        {overlays}
      </div>
    </McOpsContext.Provider>
  );
};
